import React, { useEffect, useMemo, useRef, useState } from 'react';
import { trimLeadingNetWorth } from '../utils/net-worth-series';
import { Card, CardContent, Box, Typography, ToggleButtonGroup, ToggleButton, Skeleton, Button, Chip, Switch, FormControlLabel, useTheme, alpha, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import * as d3 from 'd3';
import { snapshotsService } from '../services/snapshots-service';
import { accountsService } from '../services/accounts-service';
import { NetWorthHistoryPoint } from '../types/api';
import { baseMoney, useBaseCurrency } from '../stores/currency-store';

type RangeKey = '1m' | '6m' | 'ytd' | '12m' | '3y' | '5y';

// Net worth over time: a converted total, so base currency.
const formatCurrency = (n: number) => baseMoney(n, 0);

export const NetWorthD3Chart: React.FC = () => {
  const baseCurrency = useBaseCurrency();
  const theme = useTheme();
  const [range, setRange] = useState<RangeKey>('12m');
  const [data, setData] = useState<NetWorthHistoryPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewDomain, setViewDomain] = useState<[Date, Date] | null>(null);
  const [activeSeries, setActiveSeries] = useState<('net' | 'assets' | 'debts' | 'investments')[]>(['net']);
  const [showAccountOverlays, setShowAccountOverlays] = useState(false);
  const [accountList, setAccountList] = useState<{ id: string; name: string; color: string; lineStyle: string; owner_email?: string; account_type?: string }[]>([]);
  const [activeAccounts, setActiveAccounts] = useState<string[]>([]);
  const [accountSeries, setAccountSeries] = useState<Record<string, { date: Date, value: number }[]>>({});
  const [dimensions, setDimensions] = useState({ width: 0, height: 380 });
  const [selectedMember, setSelectedMember] = useState<string>('all');
  const [isMobile, setIsMobile] = useState(false);

  // Resize observer
  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver(entries => {
      if (!entries || entries.length === 0) return;
      const { width } = entries[0].contentRect;
      const mobile = width < 600;
      setIsMobile(mobile);
      setDimensions({ width, height: mobile ? 280 : 380 });
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    let start = new Date(end);
    if (range === '1m') start.setMonth(end.getMonth() - 1);
    else if (range === '6m') start.setMonth(end.getMonth() - 6);
    // Year to date: 1 January of the CURRENT year, not the last twelve months.
    // In August those differ by seven months, and "how am I doing this year"
    // is the question a trailing-twelve window cannot answer.
    else if (range === 'ytd') start = new Date(end.getFullYear(), 0, 1);
    else if (range === '12m') start.setMonth(end.getMonth() - 12);
    else if (range === '3y') start.setFullYear(end.getFullYear() - 3);
    else start.setFullYear(end.getFullYear() - 5);
    const pad = (v: number) => String(v).padStart(2, '0');
    const s = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
    const e = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;
    return { startDate: s, endDate: e };
  }, [range]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setViewDomain(null);
    snapshotsService.getLedgerNetWorthHistory(startDate, endDate)
      .then(r => {
        if (!mounted) return;
        // Start where the household's history starts, not on a flat ~$0 run.
        setData(trimLeadingNetWorth(r.data || []));
      })
      .catch(() => {
        if (!mounted) return;
        setData([]);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    accountsService.getAccountsWithBalances().then(res => {
      if (!mounted) return;
      // Distinct, vibrant colors that are easy to differentiate
      const baseColors = [
        '#FF6B6B', // Red
        '#4ECDC4', // Teal
        '#FFD93D', // Yellow
        '#6C5CE7', // Purple
        '#A8E6CF', // Mint
        '#FF8B94', // Pink
        '#FFA07A', // Salmon
        '#95E1D3', // Aqua
        '#F38181', // Coral
        '#AA96DA', // Lavender
        '#FCBAD3', // Rose
        '#74B9FF', // Sky Blue
      ];
      // Different line styles to make accounts more distinguishable
      const lineStyles = [
        '', // solid
        '8,4', // dashed
        '4,4', // dotted
        '12,4,4,4', // dash-dot
        '12,4,4,4,4,4', // dash-dot-dot
        '2,2', // fine dotted
        '10,2', // long dash
        '6,2,2,2', // dash-dot small
      ];
      const list = (res.data || []).map((a, i) => ({
        id: a.id,
        name: a.name,
        color: baseColors[i % baseColors.length],
        lineStyle: lineStyles[i % lineStyles.length],
        owner_email: a.owner_email,
        account_type: a.account_type
      }));
      setAccountList(list);
      // Initially select top 8 non-debt accounts (debts dwarf other account values)
      const nonDebtAccounts = list.filter(a => a.account_type !== 'Debt');
      // Safety: if no non-debt accounts, select all accounts
      const accountsToSelect = nonDebtAccounts.length > 0 ? nonDebtAccounts : list;
      const selectedIds = accountsToSelect.slice(0, 8).map(a => a.id);
      setActiveAccounts(selectedIds);
    }).catch(() => { });
    return () => { mounted = false };
  }, [startDate, endDate]);

  // Filter accounts based on selected member
  const filteredAccountList = useMemo(() => {
    if (selectedMember === 'all') return accountList;
    return accountList.filter(a => a.owner_email === selectedMember);
  }, [accountList, selectedMember]);

  // Update active accounts when member selection changes
  useEffect(() => {
    // Reset active accounts to filtered list (exclude debts) when member changes
    let filtered = selectedMember === 'all' ? accountList : accountList.filter(a => a.owner_email === selectedMember);
    // Exclude debt accounts by default as they dwarf other values
    filtered = filtered.filter(a => a.account_type !== 'Debt');
    setActiveAccounts(filtered.slice(0, 8).map(a => a.id));
  }, [selectedMember, accountList]);

  // Auto-switch to 5y view when toggling to accounts for better visibility
  useEffect(() => {
    if (showAccountOverlays && range !== '5y') {
      setRange('5y');
    }
  }, [showAccountOverlays]);

  // Unique members for dropdown
  const members = useMemo(() => {
    const emails = new Set(accountList.map(a => a.owner_email).filter(Boolean));
    return Array.from(emails) as string[];
  }, [accountList]);

  useEffect(() => {
    if (!dimensions.width) return;
    if (!data.length) return;
    // If showing accounts but no account data loaded yet, skip rendering
    if (showAccountOverlays && Object.keys(accountSeries).length === 0) return;

    const svg = d3.select(svgRef.current);
    const { width, height } = dimensions;
    const margin = isMobile
      ? { top: 15, right: 10, bottom: 25, left: 50 }
      : { top: 20, right: 20, bottom: 30, left: 60 };

    svg.selectAll('*').remove();

    // Gradients
    const defs = svg.append('defs');
    const gradient = defs.append('linearGradient')
      .attr('id', 'area-gradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '0%').attr('y2', '100%');
    // Reason: In dark mode, primary blue blends with navy background. Use a bright
    // cyan/teal for high contrast. In light mode, keep the navy primary.
    const chartNetColor = theme.palette.mode === 'dark' ? '#4dd0e1' : theme.palette.primary.main;
    gradient.append('stop').attr('offset', '0%').attr('stop-color', chartNetColor).attr('stop-opacity', theme.palette.mode === 'dark' ? 0.25 : 0.3);
    gradient.append('stop').attr('offset', '100%').attr('stop-color', chartNetColor).attr('stop-opacity', 0);

    const parse = (d: NetWorthHistoryPoint) => ({
      date: new Date(d.date),
      net: +d.net_worth,
      assets: +d.total_assets,
      debts: -Math.abs(+d.total_debts), // Debts as negative for visualization if needed, but usually we plot magnitude or separate lines
      investments: +d.total_investments,
    });
    const points = data.map(parse).sort((a, b) => +a.date - +b.date);

    const fullDomain = d3.extent(points, d => d.date) as [Date, Date];
    const x = d3.scaleTime()
      .domain(viewDomain || fullDomain)
      .range([margin.left, width - margin.right]);

    // Determine Y domain based on visible series
    let allValues: number[] = [];

    if (showAccountOverlays) {
      // Only include active account values when showing accounts
      Object.entries(accountSeries).forEach(([aid, series]) => {
        if (activeAccounts.includes(aid) && (!selectedMember || selectedMember === 'all' || accountList.find(a => a.id === aid)?.owner_email === selectedMember)) {
          allValues.push(...series.map(s => s.value));
        }
      });
    } else {
      // Include active aggregate series when not showing accounts
      if (activeSeries.includes('net')) allValues.push(...points.map(p => p.net));
      if (activeSeries.includes('assets')) allValues.push(...points.map(p => p.assets));
      if (activeSeries.includes('debts')) allValues.push(...points.map(p => p.debts));
      if (activeSeries.includes('investments')) allValues.push(...points.map(p => p.investments));
    }

    // Fallback: use aggregate data if no account data is available
    if (allValues.length === 0 && showAccountOverlays) {
      // Use aggregate data as fallback when account data isn't loaded yet
      if (points.length > 0) {
        allValues.push(...points.map(p => p.net));
        allValues.push(...points.map(p => p.assets));
        allValues.push(...points.map(p => p.debts));
      } else {
        allValues = [0, 1000];
      }
    } else if (allValues.length === 0) {
      allValues = [0, 1000];
    }

    const yMin = d3.min(allValues) || 0;
    const yMax = d3.max(allValues) || 0;
    // Add some padding
    const yPadding = Math.max((yMax - yMin) * 0.1, 1000); // At least 1000 padding

    const y = d3.scaleLinear()
      .domain([yMin - yPadding, yMax + yPadding])
      .range([height - margin.bottom, margin.top]);

    // Axes
    const xAxis = d3.axisBottom(x).ticks(isMobile ? 3 : 6).tickSize(0).tickPadding(8);
    const yAxis = d3.axisLeft(y)
      .ticks(isMobile ? 4 : 5)
      .tickFormat(d => new Intl.NumberFormat('en-US', {
        notation: "compact",
        compactDisplay: "short",
        style: 'currency',
        currency: baseCurrency,
        maximumFractionDigits: isMobile ? 0 : 1
      }).format(d as number))
      .tickSize(0)
      .tickPadding(isMobile ? 6 : 10);

    // Grid lines
    svg.append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(5).tickSize(-width + margin.left + margin.right).tickFormat(() => ''))
      .attr('stroke-opacity', 0.1);

    svg.append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(xAxis)
      .attr('color', theme.palette.text.secondary)
      .style('font-size', isMobile ? '10px' : '12px');

    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(yAxis)
      .attr('color', theme.palette.text.secondary)
      .style('font-size', isMobile ? '10px' : '12px');

    // Line generators
    const line = d3.line<any>()
      .x(d => x(d.date))
      .y(d => y(d.value))
      .curve(d3.curveCatmullRom.alpha(0.5));

    const area = d3.area<any>()
      .x(d => x(d.date))
      .y0(y(0)) // Fill to zero line? Or bottom?
      .y1(d => y(d.value))
      .curve(d3.curveCatmullRom.alpha(0.5));

    // Draw Aggregate Series (only when account overlays are off)
    if (!showAccountOverlays) {
      if (activeSeries.includes('net')) {
        const series = points.map(p => ({ date: p.date, value: p.net }));
        // Area
        svg.append('path')
          .datum(series)
          .attr('fill', 'url(#area-gradient)')
          .attr('d', area.y0(y(Math.max(0, yMin))) as any); // Fill to 0 or min

        // Line
        svg.append('path')
          .datum(series)
          .attr('fill', 'none')
          .attr('stroke', chartNetColor)
          .attr('stroke-width', 2.5)
          .attr('d', line as any);
      }

      const drawAuxSeries = (key: 'assets' | 'debts' | 'investments', color: string) => {
        if (activeSeries.includes(key)) {
          const series = points.map(p => ({ date: p.date, value: (p as any)[key] }));
          svg.append('path')
            .datum(series)
            .attr('fill', 'none')
            .attr('stroke', color)
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '4,4')
            .attr('d', line as any);
        }
      };

      drawAuxSeries('assets', theme.palette.success.main);
      drawAuxSeries('debts', theme.palette.error.main);
      drawAuxSeries('investments', theme.palette.secondary.main);
    }

    // Draw Account Overlays
    if (showAccountOverlays) {
      Object.entries(accountSeries).forEach(([aid, series]) => {
        const meta = accountList.find(a => a.id === aid);
        // Filter by member and active accounts
        if (!meta || !activeAccounts.includes(aid)) return;
        if (selectedMember !== 'all' && meta.owner_email !== selectedMember) return;
        if (!series || series.length === 0) return; // Skip if no data

        const path = svg.append('path')
          .datum(series)
          .attr('fill', 'none')
          .attr('stroke', meta.color)
          .attr('stroke-width', isMobile ? 2.5 : 3.5)
          .attr('stroke-opacity', 1)
          .attr('d', line as any);

        // Apply dash pattern if specified
        if (meta.lineStyle) {
          path.attr('stroke-dasharray', meta.lineStyle);
        }
      });
    }

    // Tooltip interaction
    const tooltip = d3.select(containerRef.current).append('div')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', alpha(theme.palette.background.paper, 0.9))
      .style('backdrop-filter', 'blur(8px)')
      .style('padding', '12px')
      .style('border-radius', '12px')
      .style('box-shadow', theme.shadows[4])
      .style('border', `1px solid ${theme.palette.divider}`)
      .style('pointer-events', 'none')
      .style('font-size', '12px')
      .style('z-index', 10);

    const bisect = d3.bisector<any, Date>(d => d.date).center;
    const overlay = svg.append('rect')
      .attr('width', width - margin.left - margin.right)
      .attr('height', height - margin.top - margin.bottom)
      .attr('transform', `translate(${margin.left},${margin.top})`)
      .style('fill', 'none')
      .style('pointer-events', 'all');

    const cursorLine = svg.append('line')
      .attr('stroke', theme.palette.text.secondary)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,4')
      .style('opacity', 0);

    overlay.on('mousemove', (event) => {
      const [mx] = d3.pointer(event);
      const date = x.invert(mx + margin.left); // Adjust for transform? No, pointer is relative to node.
      // Wait, pointer on overlay (which is translated) gives coords relative to overlay?
      // Let's use d3.pointer(event, svg.node()) and adjust.
      const [svgX] = d3.pointer(event, svg.node());
      const dateX = x.invert(svgX);

      const i = bisect(points, dateX);
      const p = points[Math.min(points.length - 1, Math.max(0, i))];

      if (!p) return;

      cursorLine
        .attr('x1', x(p.date))
        .attr('x2', x(p.date))
        .attr('y1', margin.top)
        .attr('y2', height - margin.bottom)
        .style('opacity', 1);

      let content = `<strong>${p.date.toLocaleDateString()}</strong><br/>`;

      if (showAccountOverlays) {
        // Only show accounts when account overlays are on
        Object.entries(accountSeries).forEach(([aid, series]) => {
          if (!activeAccounts.includes(aid)) return;
          const meta = accountList.find(a => a.id === aid);
          if (!meta || (selectedMember !== 'all' && meta.owner_email !== selectedMember)) return;

          // Find value at this date
          const idx = bisect(series, dateX);
          const ap = series[Math.min(series.length - 1, Math.max(0, idx))];
          // Check if date is close enough (e.g. within 2 days)
          if (Math.abs(ap.date.getTime() - p.date.getTime()) < 2 * 24 * 60 * 60 * 1000) {
            content += `<div style="color:${meta.color}">${meta.name}: ${formatCurrency(ap.value)}</div>`;
          }
        });
      } else {
        // Show aggregates when account overlays are off
        if (activeSeries.includes('net')) content += `<div style="color:${chartNetColor}">Net Worth: ${formatCurrency(p.net)}</div>`;
        if (activeSeries.includes('assets')) content += `<div style="color:${theme.palette.success.main}">Assets: ${formatCurrency(p.assets)}</div>`;
        if (activeSeries.includes('debts')) content += `<div style="color:${theme.palette.error.main}">Debts: ${formatCurrency(p.debts)}</div>`;
        if (activeSeries.includes('investments')) content += `<div style="color:${theme.palette.secondary.main}">Investments: ${formatCurrency(p.investments)}</div>`;
      }

      tooltip
        .style('visibility', 'visible')
        .html(content)
        .style('left', `${x(p.date) + 10}px`)
        .style('top', `${y(p.net)}px`); // Position near net worth point?

      // Adjust tooltip position if too close to right edge
      if (x(p.date) > width - 150) {
        tooltip.style('left', `${x(p.date) - 160}px`);
      }
    });

    overlay.on('mouseleave', () => {
      tooltip.style('visibility', 'hidden');
      cursorLine.style('opacity', 0);
    });

  // baseCurrency is a dependency because the axis ticks are drawn imperatively
  // by d3 inside this effect -- without it the chart keeps the old currency
  // label until something else happens to trigger a redraw.
  }, [data, dimensions, viewDomain, activeSeries, showAccountOverlays, accountSeries, activeAccounts, filteredAccountList, selectedMember, theme, baseCurrency]);

  useEffect(() => {
    let mounted = true;
    if (!showAccountOverlays) {
      setAccountSeries({});
      return;
    }
    // For account view, fetch ALL snapshots (no date filter)
    // The date range filter might miss snapshots created on 1st of month vs query date
    snapshotsService.getSnapshotsWithAccounts({ per_page: 10000 })
      .then(res => {
        if (!mounted) return;
        const grouped: Record<string, { date: Date, value: number }[]> = {};
        (res.data.data || []).forEach((s) => {
          const aid = s.account_id;
          const d = new Date(s.date);
          const v = parseFloat(s.balance);
          if (!grouped[aid]) grouped[aid] = [];
          grouped[aid].push({ date: d, value: v });
        });
        Object.values(grouped).forEach(arr => arr.sort((a, b) => +a.date - +b.date));
        setAccountSeries(grouped);
      }).catch((err) => {
        console.error('Failed to load account snapshots:', err);
      });
    return () => { mounted = false };
  }, [showAccountOverlays]); // Removed startDate, endDate - we fetch all snapshots

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 3, gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">Net Worth Trends</Typography>
            <Typography variant="body2" color="text.secondary">Track your financial growth over time</Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 1 }, alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              size="small"
              value={range}
              exclusive
              onChange={(_, v) => v && setRange(v)}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                '& .MuiToggleButton-root': {
                  flex: { xs: 1, sm: 'initial' }
                }
              }}
            >
              <ToggleButton value="1m">1M</ToggleButton>
              <ToggleButton value="6m">6M</ToggleButton>
              <ToggleButton value="ytd">YTD</ToggleButton>
              <ToggleButton value="12m">1Y</ToggleButton>
              <ToggleButton value="3y">3Y</ToggleButton>
              <ToggleButton value="5y">ALL</ToggleButton>
            </ToggleButtonGroup>

            <FormControlLabel
              control={<Switch checked={showAccountOverlays} onChange={(_, v) => setShowAccountOverlays(v)} />}
              label="Accounts"
              sx={{ m: 0 }}
            />

            {showAccountOverlays && members.length > 0 && (
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                <InputLabel>Member</InputLabel>
                <Select
                  value={selectedMember}
                  label="Member"
                  onChange={(e) => setSelectedMember(e.target.value)}
                >
                  <MenuItem value="all">All Members</MenuItem>
                  {members.map(email => (
                    <MenuItem key={email} value={email}>{email}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {!showAccountOverlays && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {[
              { key: 'net' as const, label: 'Net Worth', color: theme.palette.mode === 'dark' ? '#4dd0e1' : theme.palette.primary.main },
              { key: 'assets' as const, label: 'Assets', color: theme.palette.success.main },
              { key: 'debts' as const, label: 'Debts', color: theme.palette.error.main },
              { key: 'investments' as const, label: 'Investments', color: theme.palette.secondary.main },
            ].map(s => (
              <Chip
                key={s.key}
                label={s.label}
                onClick={() => setActiveSeries(prev => prev.includes(s.key) ? prev.filter(k => k !== s.key) : [...prev, s.key])}
                sx={{
                  bgcolor: activeSeries.includes(s.key) ? alpha(s.color, 0.1) : 'transparent',
                  color: activeSeries.includes(s.key) ? s.color : 'text.disabled',
                  borderColor: activeSeries.includes(s.key) ? s.color : 'divider',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  fontWeight: 600
                }}
                size="small"
              />
            ))}
          </Box>
        )}

        <Box sx={{ flexGrow: 1, minHeight: 300, position: 'relative' }} ref={containerRef}>
          {loading ? (
            <Skeleton variant="rectangular" height="100%" width="100%" sx={{ borderRadius: 2 }} />
          ) : (
            <svg ref={svgRef} width={dimensions.width} height={dimensions.height} style={{ overflow: 'visible' }} />
          )}
        </Box>

        {showAccountOverlays && (
          <Box sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            mt: 2,
            maxHeight: { xs: 160, sm: 120 },
            overflowY: 'auto',
            overflowX: 'hidden'
          }}>
            {filteredAccountList.map(a => (
              <Chip
                key={a.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {!isMobile && (
                      <svg width="20" height="12" style={{ marginRight: 4 }}>
                        <line
                          x1="0"
                          y1="6"
                          x2="20"
                          y2="6"
                          stroke={activeAccounts.includes(a.id) ? '#fff' : a.color}
                          strokeWidth="2.5"
                          strokeDasharray={a.lineStyle}
                        />
                      </svg>
                    )}
                    <Typography variant="caption" component="span" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      {a.name}
                    </Typography>
                  </Box>
                }
                onClick={() => setActiveAccounts(prev => prev.includes(a.id) ? prev.filter(id => id !== a.id) : [...prev, a.id])}
                sx={{
                  bgcolor: activeAccounts.includes(a.id) ? a.color : 'transparent',
                  color: activeAccounts.includes(a.id) ? '#fff' : 'text.primary',
                  opacity: activeAccounts.includes(a.id) ? 1 : 0.5,
                  border: `2px solid ${activeAccounts.includes(a.id) ? a.color : theme.palette.divider}`,
                  fontWeight: activeAccounts.includes(a.id) ? 600 : 400,
                  minWidth: 0,
                  px: { xs: 0.75, sm: 1 },
                  height: { xs: '28px', sm: '32px' }
                }}
                size="small"
              />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
